// /srv/docker/develop/strike.hu_51200-51220/next/components/pages/BirthdayPage.jsx

"use client";

import {
    CakeSlice,
    PartyPopper,
    Target,
    DoorOpen,
    Users,
    CalendarCheck,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

const translations = {
    hu: {
        heroTitle:
            "szülinapi program és születésnapi helyszín Budapesten",

        heroSubtitle: (
            <>
                ne csak ünnepeljetek.
                <br />
                játsszatok is.
            </>
        ),

        heroDescription:
            "ha különleges szülinapi programot keresel Budapesten, a strike-ban 8 interaktív ügyességi pálya, 48 dobás és egy közös kihívás vár az egész társaságra.",

        heroSecond:
            "barátokkal, családdal vagy egy nagyobb társasággal is jöhettek – nálunk az ünneplésből közös élmény lesz.",

        bookingButton:
            "időpontot foglalok",

        heroImageAlt:
            "szülinapi program a strike interaktív ügyességi játékcentrumában Budapesten",

        experienceTitle:
            "egy szülinap, ahol történik is valami",

        experienceFirst:
            "a strike nem egy hagyományos születésnapi helyszín, ahol csak leültök egy asztal köré.",

        experienceSecond:
            "a játék során 8 különböző pályán haladtok végig, minden pályán 6 dobással. ez összesen 48 lehetőség arra, hogy célba találjatok, pontokat szerezzetek és megelőzzétek egymást.",

        experienceThird:
            "az eredményeket végig követhetitek, így hamar kiderül, ki a társaság legpontosabb játékosa.",

        howTitle:
            "így működik a játék",

        howIntroduction:
            "egyszerű elkezdeni, de annál nehezebb tökéletesen teljesíteni.",

        howItems: [
            {
                title:
                    "8 különleges pálya",

                description:
                    "nyolc eltérő ügyességi kihívás vár rátok a strike zone-ban.",
            },
            {
                title:
                    "6 dobás pályánként",

                description:
                    "minden pályán hat lehetőségetek van pontokat gyűjteni.",
            },
            {
                title:
                    "48 dobás összesen",

                description:
                    "egy teljes játék során minden játékos összesen 48 dobást teljesít.",
            },
            {
                title:
                    "közös verseny",

                description:
                    "figyeljétek egymás eredményeit, szurkoljatok és derüljön ki, ki végez az élen.",
            },
        ],

        normalBirthdayTitle:
            "szülinap a strike zone-ban",

        normalBirthdayDescription:
            "ha elsősorban játszani szeretnétek, foglaljatok egy normál strike időpontot. végigmentek a 8 pályán, összeméritek az ügyességeteket, és együtt élitek át a teljes strike élményt.",

        normalBirthdaySecond:
            "jó választás baráti születésnaphoz, kisebb társasághoz vagy akkor, ha maga a játék a program fő része.",

        normalBookingButton:
            "strike foglalás",

        privateRoomTitle:
            "saját tér az ünnepléshez",

        privateRoomDescription:
            "ha a játék mellett saját különtermet is szeretnétek, válasszátok a private roomot. a terem és a benne található játékok a foglalás teljes ideje alatt kizárólag a ti társaságotoké.",

        privateRoomSecond:
            "a private room 6–10 fős társaságok számára foglalható, és kifejezetten jól működik születésnapokra, baráti összejövetelekre és különleges alkalmakra.",

        privateRoomButton:
            "private room részletek",

        forWhoTitle:
            "kinek való?",

        forWhoIntroduction:
            "a strike-ban nem az életkor, hanem a játék kedve számít.",

        forWhoItems: [
            "baráti társaságoknak",
            "családi születésnapokra",
            "fiataloknak és felnőtteknek",
            "pároknak és kisebb társaságoknak",
            "nagyobb ünnepléshez private room foglalással",
        ],

        indoorTitle:
            "beltéri szülinapi program egész évben",

        indoorDescription:
            "esik az eső? hideg van? túl meleg van odakint? a strike beltéri program, ezért a születésnap nem függ az időjárástól.",

        indoorSecond:
            "Budapest XIII. kerületében, a Pannónia utcában várunk benneteket, néhány percre a Westendtől és a Nyugati pályaudvar környékétől.",

        locationButton:
            "megközelítés",

        finalTitle:
            "készen álltok?",

        finalDescription:
            "8 pálya. 48 dobás. egy születésnap, amiről lesz mit mesélni.",

        finalSecond:
            "hozd a társaságot. a játékot mi adjuk.",

        finalButton:
            "foglalj időpontot",

        strikeZoneImageAlt:
            "strike zone ügyességi pályák születésnapi programhoz Budapesten",

        privateRoomImageAlt:
            "strike private room születésnapi helyszín Budapesten",
    },

    en: {
        heroTitle:
            "birthday experience in Budapest",

        heroSubtitle: (
            <>
                don't just celebrate.
                <br />
                play together.
            </>
        ),

        heroDescription:
            "if you're looking for a different birthday experience in Budapest, strike offers 8 interactive skill courses, 48 throws and a shared challenge for the whole group.",

        heroSecond:
            "come with friends, family or a larger group and turn the celebration into something you can experience together.",

        bookingButton:
            "book now",

        heroImageAlt:
            "birthday experience at the strike interactive skill game centre in Budapest",

        experienceTitle:
            "a birthday where something actually happens",

        experienceFirst:
            "strike is not a traditional birthday venue where everyone simply sits around a table.",

        experienceSecond:
            "during the game, you make your way through 8 different courses with 6 throws on each one. that's 48 chances to hit the targets, collect points and beat your friends.",

        experienceThird:
            "you can follow the scores throughout the game, so it won't take long to discover who is the most accurate player in the group.",

        howTitle:
            "how the game works",

        howIntroduction:
            "easy to understand, much harder to master.",

        howItems: [
            {
                title:
                    "8 unique courses",

                description:
                    "eight different skill challenges await you in the strike zone.",
            },
            {
                title:
                    "6 throws per course",

                description:
                    "you have six opportunities to score points on every course.",
            },
            {
                title:
                    "48 throws in total",

                description:
                    "each player completes a total of 48 throws during a full game.",
            },
            {
                title:
                    "friendly competition",

                description:
                    "follow each other's scores, cheer each other on and see who finishes on top.",
            },
        ],

        normalBirthdayTitle:
            "birthday in the strike zone",

        normalBirthdayDescription:
            "if playing is the main event, simply book a regular strike session. complete all 8 courses, test your skills against each other and experience the full strike game together.",

        normalBirthdaySecond:
            "a great choice for birthdays with friends, smaller groups or whenever the game itself is the focus of the celebration.",

        normalBookingButton:
            "book strike",

        privateRoomTitle:
            "your own space to celebrate",

        privateRoomDescription:
            "if you'd also like a private space alongside the game, choose the private room. throughout your booking, the room and its games are reserved exclusively for your group.",

        privateRoomSecond:
            "the private room is available for groups of 6 to 10 guests and is ideal for birthdays, gatherings with friends and special occasions.",

        privateRoomButton:
            "private room details",

        forWhoTitle:
            "who is it for?",

        forWhoIntroduction:
            "at strike, enthusiasm matters more than age.",

        forWhoItems: [
            "groups of friends",
            "family birthdays",
            "young people and adults",
            "couples and smaller groups",
            "larger celebrations with a private room",
        ],

        indoorTitle:
            "an indoor birthday experience all year round",

        indoorDescription:
            "rainy, cold or too hot outside? strike is an indoor experience, so your birthday plans don't depend on the weather.",

        indoorSecond:
            "you'll find us on Pannónia utca in Budapest's District XIII, just a short distance from Westend and the Nyugati railway station area.",

        locationButton:
            "getting here",

        finalTitle:
            "ready?",

        finalDescription:
            "8 courses. 48 throws. one birthday worth talking about.",

        finalSecond:
            "bring the group. we'll bring the game.",

        finalButton:
            "book now",

        strikeZoneImageAlt:
            "strike zone skill courses for a birthday activity in Budapest",

        privateRoomImageAlt:
            "strike private room birthday venue in Budapest",
    },
};

export default function BirthdayPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* HERO */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex min-h-[500px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[620px] md:px-12 md:py-20">
                    <IconCircle>
                        <CakeSlice
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </IconCircle>

                    <h1 className="text-[27px] font-black lowercase leading-[1.05] md:text-[50px]">
                        {text.heroTitle}
                    </h1>

                    <p className="mt-8 text-[19px] font-black leading-[1.15] md:text-[27px]">
                        {text.heroSubtitle}
                    </p>

                    <p className="mt-7 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroDescription}
                    </p>

                    <p className="mt-6 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroSecond}
                    </p>

                    <a
                        href="/booking"
                        className="
                            button-pulse mt-9 flex h-[52px]
                            min-w-[240px]
                            cursor-pointer select-none
                            items-center justify-center
                            rounded-[6px]
                            bg-[#f5a400] px-7
                            text-[18px] font-black
                            text-black transition-transform
                            duration-200
                            hover:scale-[1.03]
                            active:translate-y-[2px]
                            active:scale-95
                            md:mt-11 md:h-[60px]
                            md:min-w-[280px]
                            md:text-[21px]
                        "
                    >
                        {text.bookingButton}
                    </a>
                </div>
            </section>

            <PageImage
                src="/v4/megerkeztel_strike.webp"
                alt={
                    text.heroImageAlt
                }
            />

            {/* ÉLMÉNY */}
            <ContentSection
                icon={
                    PartyPopper
                }
                title={
                    text.experienceTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.experienceFirst}
                    </p>

                    <p>
                        {text.experienceSecond}
                    </p>

                    <p>
                        {text.experienceThird}
                    </p>
                </div>
            </ContentSection>

            {/* HOGYAN MŰKÖDIK */}
            <section className="bg-[#f2f2f2] text-black">
                <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                    <div className="border-[3px] border-black bg-[#f2f2f2]">
                        <div className="bg-black px-6 py-9 text-center text-white md:px-12 md:py-12">
                            <Target
                                className="mx-auto h-10 w-10 md:h-12 md:w-12"
                                strokeWidth={
                                    2.5
                                }
                            />

                            <h2 className="mt-5 text-[24px] font-black lowercase leading-none md:text-[42px]">
                                {text.howTitle}
                            </h2>

                            <p className="mx-auto mt-5 max-w-[300px] text-[14px] leading-[1.3] text-white/70 md:max-w-xl md:text-[19px]">
                                {text.howIntroduction}
                            </p>
                        </div>

                        <div className="px-5 py-8 md:px-12 md:py-12">
                            <div className="border-y-[3px] border-black">
                                {text.howItems.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <div
                                            key={
                                                item.title
                                            }
                                            className={`flex items-start gap-4 py-5 md:gap-6 md:py-7 ${index !==
                                                text.howItems.length - 1
                                                ? "border-b border-black/25"
                                                : ""
                                                }`}
                                        >
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                                {String(
                                                    index +
                                                    1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </span>

                                            <div>
                                                <h3 className="text-[16px] font-black lowercase leading-[1.2] md:text-[21px]">
                                                    {item.title}
                                                </h3>

                                                <p className="mt-2 text-[14px] leading-[1.4] text-neutral-700 md:text-[18px] md:leading-[1.5]">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* NORMÁL STRIKE FOGLALÁS */}
            <ContentSection
                icon={
                    Target
                }
                title={
                    text.normalBirthdayTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.normalBirthdayDescription}
                    </p>

                    <p>
                        {text.normalBirthdaySecond}
                    </p>
                </div>

                <PrimaryButton
                    href="/booking"
                >
                    {text.normalBookingButton}
                </PrimaryButton>
            </ContentSection>

            <PageImage
                src="/v4/találd_meg_eldoradot.webp"
                alt={
                    text.strikeZoneImageAlt
                }
            />

            {/* PRIVATE ROOM */}
            <ContentSection
                icon={
                    DoorOpen
                }
                title={
                    text.privateRoomTitle
                }
                backgroundClass="bg-[#f2f2f2]"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.privateRoomDescription}
                    </p>

                    <p>
                        {text.privateRoomSecond}
                    </p>
                </div>

                <PrimaryButton
                    href={getLocalizedPath(
                        "/private-room",
                        language
                    )}
                >
                </PrimaryButton>
            </ContentSection>

            <PageImage
                src="/v4/private_room_strike_v3.webp"
                alt={
                    text.privateRoomImageAlt
                }
            />

            {/* KINEK VALÓ */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                    <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                        <div className="flex flex-col items-center text-center">
                            <IconCircle>
                                <Users
                                    style={{
                                        width:
                                            "38px",

                                        height:
                                            "38px",
                                    }}
                                    strokeWidth={
                                        2.5
                                    }
                                />
                            </IconCircle>

                            <h2 className="text-[22px] font-black lowercase leading-none md:text-[40px]">
                                {text.forWhoTitle}
                            </h2>

                            <p className="mt-6 max-w-[300px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                                {text.forWhoIntroduction}
                            </p>
                        </div>

                        <div className="mt-10 border-y-[3px] border-black md:mt-14">
                            {text.forWhoItems.map(
                                (
                                    item,
                                    index
                                ) => (
                                    <div
                                        key={
                                            item
                                        }
                                        className={`flex items-center gap-4 py-5 md:gap-6 md:py-6 ${index !==
                                            text.forWhoItems.length - 1
                                            ? "border-b border-black/25"
                                            : ""
                                            }`}
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                            {String(
                                                index +
                                                1
                                            ).padStart(
                                                2,
                                                "0"
                                            )}
                                        </span>

                                        <p className="text-[15px] font-semibold leading-[1.4] text-neutral-700 md:text-[19px]">
                                            {item}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* BELTÉRI */}
            <ContentSection
                icon={
                    CalendarCheck
                }
                title={
                    text.indoorTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.indoorDescription}
                    </p>

                    <p>
                        {text.indoorSecond}
                    </p>
                </div>

                <SecondaryButton
                    href={getLocalizedPath(
                        "/megkozelites",
                        language
                    )}
                >
                </SecondaryButton>
            </ContentSection>

            {/* ZÁRÁS */}
            <section className="bg-black text-white">
                <div className="mx-auto flex min-h-[390px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-16 text-center md:max-w-5xl md:min-h-[500px] md:px-12 md:py-20">
                    <PartyPopper
                        className="h-12 w-12 md:h-16 md:w-16"
                        strokeWidth={
                            2.2
                        }
                    />

                    <h2 className="mt-7 text-[27px] font-black lowercase leading-none md:text-[48px]">
                        {text.finalTitle}
                    </h2>

                    <p className="mt-7 max-w-[310px] text-[17px] font-black leading-[1.3] md:max-w-2xl md:text-[25px]">
                        {text.finalDescription}
                    </p>

                    <p className="mt-5 max-w-[310px] text-[15px] leading-[1.3] text-white/70 md:max-w-xl md:text-[20px]">
                        {text.finalSecond}
                    </p>

                    <PrimaryButton
                        href="/booking"
                    >
                        {text.finalButton}
                    </PrimaryButton>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function IconCircle({
    children,
}) {
    return (
        <div
            className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
            style={{
                width:
                    "80px",

                height:
                    "80px",

                minWidth:
                    "80px",

                maxWidth:
                    "80px",

                minHeight:
                    "80px",

                maxHeight:
                    "80px",

                boxSizing:
                    "border-box",
            }}
        >
            {children}
        </div>
    );
}

function ContentSection({
    icon: Icon,
    title,
    children,
    backgroundClass = "bg-[#dedede]",
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div className="mx-auto flex min-h-[470px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[590px] md:px-12 md:py-20">
                <IconCircle>
                    <Icon
                        style={{
                            width:
                                "38px",

                            height:
                                "38px",
                        }}
                        strokeWidth={
                            2.5
                        }
                    />
                </IconCircle>

                <h2 className="text-[21px] font-black lowercase leading-[1.1] md:text-[36px]">
                    {title}
                </h2>

                {children}
            </div>
        </section>
    );
}

function PrimaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className={`
                ${href === "/booking" ? "button-pulse" : ""} mt-9 flex h-[52px]
                min-w-[240px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                bg-[#f5a400] px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[280px]
                md:text-[21px]
            `}
        >
            {children}
        </a>
    );
}

function SecondaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className="
                mt-9 flex h-[52px]
                min-w-[220px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                border-[3px] border-black
                bg-transparent px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[260px]
                md:text-[21px]
            "
        >
            {children}
        </a>
    );
}

function PageImage({
    src,
    alt,
}) {
    return (
        <section className="w-full overflow-hidden bg-black">
            <div className="relative flex w-full items-center justify-center overflow-hidden bg-black">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 scale-[1.15] bg-cover bg-center blur-[45px]"
                    style={{
                        backgroundImage:
                            `url("${src}")`,
                    }}
                />

                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-black/20"
                />

                <img
                    src={
                        src
                    }
                    alt={
                        alt
                    }
                    className="relative z-10 block h-auto w-full max-w-[900px] object-contain"
                />
            </div>
        </section>
    );
}